import React from 'react';
import { removeAuthCompletely } from '../core/_axios';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  copied: boolean;
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copied: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleLogout = () => {
    removeAuthCompletely();
    window.location.href = '/auth/login';
  };

  handleReload = () => {
    window.location.reload();
  };

  getErrorReport = () => {
    const { error, errorInfo } = this.state;
    const lines = [
      `Error: ${error?.message || 'Unknown error'}`,
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `UserAgent: ${navigator.userAgent}`,
      '',
      '--- Stack Trace ---',
      error?.stack || 'No stack trace available',
    ];
    if (errorInfo?.componentStack) {
      lines.push('', '--- Component Stack ---', errorInfo.componentStack);
    }
    return lines.join('\n');
  };

  handleCopy = () => {
    navigator.clipboard.writeText(this.getErrorReport()).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="card max-w-[600px] w-full m-8">
            <div className="card-body flex flex-col gap-5 p-10">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  Something went wrong
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  An unexpected error occurred. Please copy the error details below and send them to support, then try reloading or signing out.
                </p>
              </div>
              <div className="relative">
                <pre className="text-xs text-gray-700 bg-gray-100 p-4 rounded-lg overflow-auto max-h-[300px] whitespace-pre-wrap break-all font-mono select-all">
                  {this.getErrorReport()}
                </pre>
                <button
                  onClick={this.handleCopy}
                  className="absolute top-2 right-2 btn btn-xs btn-light"
                >
                  {this.state.copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={this.handleReload}
                  className="btn btn-primary"
                >
                  Reload Page
                </button>
                <button
                  onClick={this.handleLogout}
                  className="btn btn-light"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
